import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { firestore } from '../admin/FirebaseConfig';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const ApplyLoan = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    dob: '',
    loanAmount: '',
    loanPurpose: '',
    employmentType: '',
    monthlyIncome: '',
    address: '',
    panCard: '',
    aadhaarCard: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  
  // Refs for Bootstrap modals
  const loanModalRef = useRef(null);
  const successModalRef = useRef(null);
  const bootstrapScriptRef = useRef(null);

  useEffect(() => {
    // Initialize modals after Bootstrap is loaded
    const initializeModals = () => {
      if (typeof window !== 'undefined' && window.bootstrap) {
        const loanModalElement = document.getElementById('loanApplicationModal');
        const successModalElement = document.getElementById('successModal');
        
        if (loanModalElement) {
          loanModalRef.current = new window.bootstrap.Modal(loanModalElement);
        }
        if (successModalElement) {
          successModalRef.current = new window.bootstrap.Modal(successModalElement);
        }
      }
    };

    // Check if Bootstrap is already loaded
    if (typeof window !== 'undefined' && window.bootstrap) {
      initializeModals();
    } else {
      // Add Bootstrap JS dynamically if not already loaded
      const bootstrapScript = document.createElement('script');
      bootstrapScript.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/js/bootstrap.bundle.min.js';
      bootstrapScript.async = true;
      bootstrapScript.onload = initializeModals;
      document.body.appendChild(bootstrapScript);
      bootstrapScriptRef.current = bootstrapScript;
    }

    return () => {
      // Cleanup: remove dynamically added script
      if (bootstrapScriptRef.current) {
        document.body.removeChild(bootstrapScriptRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
    
    // Clear error when user starts typing
    if (errors[id]) {
      setErrors({ ...errors, [id]: '' });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Full Name is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Valid Email is required';
      if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Valid 10-digit Phone Number is required';
      
      // Validate age (must be at least 18)
      if (!formData.dob) {
        newErrors.dob = 'Date of Birth is required';
      } else {
        const today = new Date();
        const birthDate = new Date(formData.dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        if (age < 18) {
          newErrors.dob = 'You must be at least 18 years old';
        }
      }
    }
    
    if (step === 2) {
      if (!formData.loanAmount || isNaN(formData.loanAmount) || formData.loanAmount <= 0) 
        newErrors.loanAmount = 'Valid Loan Amount is required';
      if (!formData.loanPurpose) newErrors.loanPurpose = 'Select Loan Purpose';
      if (!formData.employmentType) newErrors.employmentType = 'Select Employment Type';
      if (!formData.monthlyIncome || isNaN(formData.monthlyIncome) || formData.monthlyIncome <= 0) 
        newErrors.monthlyIncome = 'Valid Monthly Income is required';
    }
    
    if (step === 3) {
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panCard)) 
        newErrors.panCard = 'Valid PAN Card number required (e.g. ABCDE1234F)';
      if (!/^\d{12}$/.test(formData.aadhaarCard)) 
        newErrors.aadhaarCard = 'Valid 12-digit Aadhaar number required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      
      // Scroll to top of form when changing steps
      const modalBody = document.querySelector('.modal-body');
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    
    // Scroll to top of form when changing steps
    const modalBody = document.querySelector('.modal-body');
    if (modalBody) {
      modalBody.scrollTop = 0;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;

    setSubmitting(true);

    // Generate a unique application number
    const appNumber = `ABFL${Date.now().toString().slice(-6)}${Math.floor(100 + Math.random() * 900)}`;
    setApplicationNumber(appNumber);

    try {
      await addDoc(collection(firestore, 'loanApplications'), {
        ...formData,
        applicationNumber: appNumber,
        status: 'Pending',
        createdAt: Timestamp.now(),
      });

      // Close the form modal and show success modal
      if (loanModalRef.current) {
        loanModalRef.current.hide();
      }
      
      // Show success message after a small delay
      setTimeout(() => {
        if (successModalRef.current) {
          successModalRef.current.show();
        }
      }, 500);

    } catch (error) {
      console.error('Error adding document: ', error);
      alert('Error submitting the form. Please try again.');
    }

    setSubmitting(false);
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      dob: '',
      loanAmount: '',
      loanPurpose: '',
      employmentType: '',
      monthlyIncome: '',
      address: '',
      panCard: '',
      aadhaarCard: '',
    });
    setCurrentStep(1);
    setErrors({});
  };

  const handleCloseModal = () => {
    resetForm();
    
    // Hide success modal
    if (successModalRef.current) {
      successModalRef.current.hide();
    }
    
    // Navigate to home page
    navigate('/');
  };

  const handleSuccessButtonClick = () => {
    resetForm();
    
    // Hide success modal
    if (successModalRef.current) {
      successModalRef.current.hide();
    }
    
    // Navigate to home page
    navigate('/');
  };

  return (
    <div className="container mt-5 mb-5">
      {/* Button to trigger modal */}
     

      {/* Loan Application Modal */}
      <div className="modal fade" id="loanApplicationModal" tabIndex="-1" aria-labelledby="loanApplicationModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
            <div className="modal-header py-4" style={{ background: 'linear-gradient(135deg, #c91429, #e83e3e)' }}>
              <h5 className="modal-title fs-3 fw-bold text-white" id="loanApplicationModalLabel">
                <i className="fas fa-hand-holding-usd me-2"></i> Apply for a Loan
              </h5>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                data-bs-dismiss="modal" 
                aria-label="Close"
                onClick={resetForm}
              ></button>
            </div>
            <div className="modal-body p-4">
              {/* Progress Bar */}
              <div className="progress-container mb-5">
                <div className="progress-bar-container d-flex justify-content-between position-relative mb-3">
                  {[1, 2, 3].map(step => (
                    <div key={step} className="step-circle d-flex align-items-center justify-content-center rounded-circle" 
                      style={{ 
                        width: '50px', 
                        height: '50px', 
                        backgroundColor: currentStep >= step ? '#c91429' : '#e9ecef',
                        color: currentStep >= step ? 'white' : '#6c757d',
                        fontWeight: 'bold',
                        zIndex: 2,
                        border: '4px solid white',
                        boxShadow: '0 0 0 2px #dee2e6'
                      }}>
                      {step}
                    </div>
                  ))}
                  <div className="progress-bar-background position-absolute w-100" style={{ top: '25px', height: '4px', backgroundColor: '#e9ecef' }}>
                    <div className="progress-bar-fill h-100" style={{ 
                      width: `${(currentStep - 1) * 50}%`, 
                      backgroundColor: '#c91429',
                      transition: 'width 0.5s ease'
                    }}></div>
                  </div>
                </div>
                <div className="d-flex justify-content-between text-muted">
                  <span style={{ marginLeft: '-10px' }}>Personal Info</span>
                  <span>Loan Details</span>
                  <span style={{ marginRight: '-10px' }}>Documents</span>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Step 1: Personal Information */}
                {currentStep === 1 && (
                  <div className="step-content">
                    <h4 className="mb-4" style={{ color: '#c91429' }}>Personal Information</h4>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label htmlFor="fullName" className="form-label fw-semibold">Full Name <span className="text-danger">*</span></label>
                        <input type="text" className={`form-control ${errors.fullName ? 'is-invalid' : ''}`} 
                          id="fullName" value={formData.fullName} onChange={handleChange} 
                          style={{ padding: '12px', borderRadius: '10px' }} />
                        <div className="invalid-feedback">{errors.fullName}</div>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="email" className="form-label fw-semibold">Email <span className="text-danger">*</span></label>
                        <input type="email" className={`form-control ${errors.email ? 'is-invalid' : ''}`} 
                          id="email" value={formData.email} onChange={handleChange} 
                          style={{ padding: '12px', borderRadius: '10px' }} />
                        <div className="invalid-feedback">{errors.email}</div>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="phone" className="form-label fw-semibold">Phone Number <span className="text-danger">*</span></label>
                        <input type="tel" className={`form-control ${errors.phone ? 'is-invalid' : ''}`} 
                          id="phone" value={formData.phone} onChange={handleChange} 
                          style={{ padding: '12px', borderRadius: '10px' }} 
                          maxLength="10" />
                        <div className="invalid-feedback">{errors.phone}</div>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="dob" className="form-label fw-semibold">Date of Birth <span className="text-danger">*</span></label>
                        <input type="date" className={`form-control ${errors.dob ? 'is-invalid' : ''}`} 
                          id="dob" value={formData.dob} onChange={handleChange} 
                          style={{ padding: '12px', borderRadius: '10px' }}
                          max={new Date().toISOString().split('T')[0]} />
                        <div className="invalid-feedback">{errors.dob}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Loan Details */}
                {currentStep === 2 && (
                  <div className="step-content">
                    <h4 className="mb-4" style={{ color: '#c91429' }}>Loan Details</h4>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label htmlFor="loanAmount" className="form-label fw-semibold">Loan Amount (₹) <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}>
                            <i className="fas fa-rupee-sign"></i>
                          </span>
                          <input type="number" className={`form-control ${errors.loanAmount ? 'is-invalid' : ''}`} 
                            id="loanAmount" value={formData.loanAmount} onChange={handleChange} 
                            style={{ borderTopRightRadius: '10px', borderBottomRightRadius: '10px', padding: '12px' }} 
                            min="1000" step="1000" />
                          <div className="invalid-feedback">{errors.loanAmount}</div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="loanPurpose" className="form-label fw-semibold">Purpose of Loan <span className="text-danger">*</span></label>
                        <select className={`form-select ${errors.loanPurpose ? 'is-invalid' : ''}`} 
                          id="loanPurpose" value={formData.loanPurpose} onChange={handleChange}
                          style={{ padding: '12px', borderRadius: '10px' }}>
                          <option value="">Choose...</option>
                          <option value="personal">Personal</option>
                          <option value="home">Home</option>
                          <option value="education">Education</option>
                          <option value="car">Car</option>
                          <option value="business">Digital business loan</option>
                          <option value="loanAgainstBond">Loan Against Bond</option>
                          <option value="educationBond">Education Loan Against Bond</option>
                        </select>
                        <div className="invalid-feedback">{errors.loanPurpose}</div>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="employmentType" className="form-label fw-semibold">Employment Type <span className="text-danger">*</span></label>
                        <select className={`form-select ${errors.employmentType ? 'is-invalid' : ''}`} 
                          id="employmentType" value={formData.employmentType} onChange={handleChange}
                          style={{ padding: '12px', borderRadius: '10px' }}>
                          <option value="">Choose...</option>
                          <option value="salaried">Salaried</option>
                          <option value="selfEmployed">Self-Employed</option>
                        </select>
                        <div className="invalid-feedback">{errors.employmentType}</div>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="monthlyIncome" className="form-label fw-semibold">Monthly Income (₹) <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}>
                            <i className="fas fa-rupee-sign"></i>
                          </span>
                          <input type="number" className={`form-control ${errors.monthlyIncome ? 'is-invalid' : ''}`} 
                            id="monthlyIncome" value={formData.monthlyIncome} onChange={handleChange} 
                            style={{ borderTopRightRadius: '10px', borderBottomRightRadius: '10px', padding: '12px' }} 
                            min="0" step="1000" />
                          <div className="invalid-feedback">{errors.monthlyIncome}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Document Details */}
                {currentStep === 3 && (
                  <div className="step-content">
                    <h4 className="mb-4" style={{ color: '#c91429' }}>Document Details</h4>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label htmlFor="panCard" className="form-label fw-semibold">PAN Card <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}>
                            <i className="fas fa-id-card"></i>
                          </span>
                          <input type="text" className={`form-control ${errors.panCard ? 'is-invalid' : ''}`} 
                            id="panCard" value={formData.panCard} onChange={handleChange} placeholder="ABCDE1234F" 
                            style={{ borderTopRightRadius: '10px', borderBottomRightRadius: '10px', padding: '12px' }} 
                            maxLength="10" />
                          <div className="invalid-feedback">{errors.panCard}</div>
                        </div>
                        <small className="form-text text-muted">Format: ABCDE1234F</small>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="aadhaarCard" className="form-label fw-semibold">Aadhaar Card <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}>
                            <i className="fas fa-address-card"></i>
                          </span>
                          <input type="text" className={`form-control ${errors.aadhaarCard ? 'is-invalid' : 'is-valid'}`} 
                            id="aadhaarCard" value={formData.aadhaarCard} onChange={handleChange} placeholder="123456789012" 
                            style={{ borderTopRightRadius: '10px', borderBottomRightRadius: '10px', padding: '12px' }} 
                            maxLength="12" />
                          <div className="invalid-feedback">{errors.aadhaarCard}</div>
                        </div>
                        <small className="form-text text-muted">12-digit number</small>
                      </div>

                      <div className="col-12">
                        <label htmlFor="address" className="form-label fw-semibold">Address <span className="text-danger">*</span></label>
                        <textarea className={`form-control ${errors.address ? 'is-invalid' : ''}`} 
                          id="address" rows="3" value={formData.address} onChange={handleChange}
                          style={{ padding: '12px', borderRadius: '10px' }}></textarea>
                        <div className="invalid-feedback">{errors.address}</div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="modal-footer mt-4 px-0">
                  {currentStep > 1 && (
                    <button type="button" className="btn btn-outline-secondary px-4 py-2" onClick={prevStep}>
                      <i className="fas fa-arrow-left me-2"></i> Previous
                    </button>
                  )}
                  
                  {currentStep < 3 ? (
                    <button type="button" className="btn px-4 py-2" onClick={nextStep} style={{
                      background: 'linear-gradient(135deg, #c91429, #e83e3e)',
                      color: 'white',
                      border: 'none'
                    }}>
                      Next <i className="fas fa-arrow-right ms-2"></i>
                    </button>
                  ) : (
                    <button type="submit" className="btn px-4 py-2" disabled={submitting} style={{
                      background: 'linear-gradient(135deg, #c91429, #e83e3e)',
                      color: 'white',
                      border: 'none'
                    }}>
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i> Submit Application
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <div className="modal fade" id="successModal" tabIndex="-1" aria-labelledby="successModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content shadow-lg" style={{ borderRadius: '20px' }}>
            <div className="modal-header py-4 text-white" style={{ 
              background: 'linear-gradient(135deg, #c91429, #e83e3e)',
              borderRadius: '20px 20px 0 0'
            }}>
              <h5 className="modal-title fs-4" id="successModalLabel">
                <i className="fas fa-check-circle me-2"></i> Application Submitted
              </h5>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                data-bs-dismiss="modal" 
                aria-label="Close" 
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body text-center py-5">
              <div className="py-3">
                <div className="success-icon mb-4">
                  <i className="fas fa-check-circle" style={{ fontSize: '5rem', color: '#c91429' }}></i>
                </div>
                <h3 className="mt-3" style={{ color: '#c91429' }}>Congratulations!</h3>
                <p className="lead">Your loan application has been submitted successfully.</p>
                <div className="alert alert-info fs-5 mt-4">
                  <strong>Application Number:</strong> {applicationNumber}
                </div>
                <p className="mt-4">Thank you for your interest. Our executive will contact you soon.</p>
              </div>
            </div>
            <div className="modal-footer border-0">
              <button 
                type="button" 
                className="btn px-4 py-2 mx-auto" 
                onClick={handleSuccessButtonClick}
                style={{
                  background: 'linear-gradient(135deg, #c91429, #e83e3e)',
                  color: 'white',
                  border: 'none'
                }}
              >
                <i className="fas fa-thumbs-up me-2"></i> Great, Thank You!
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyLoan;